import { useQuery } from '@tanstack/react-query';
import type {
  DiffusedDocumentsCountDto,
  ValidatedDocumentsCountDto,
  OpenedRatioDto,
  NotConsultedCountDto,
  DashboardRecentDocumentsDto,
  UserDocumentsDto,
  FilteredDocumentsDto,
} from '@monorepo/shared';
import { apiClient } from '../lib/axios';

const dashboardApi = {
  getDiffusedCount: async (companyId: string): Promise<DiffusedDocumentsCountDto> => {
    const { data: response } = await apiClient.get<DiffusedDocumentsCountDto>(
      `/companies/${companyId}/dashboard/diffused-count`,
    );
    return response;
  },

  getValidatedCount: async (companyId: string): Promise<ValidatedDocumentsCountDto> => {
    const { data: response } = await apiClient.get<ValidatedDocumentsCountDto>(
      `/companies/${companyId}/dashboard/validated-count`,
    );
    return response;
  },

  getOpenedRatio: async (companyId: string): Promise<OpenedRatioDto> => {
    const { data: response } = await apiClient.get<OpenedRatioDto>(
      `/companies/${companyId}/dashboard/opened-ratio`,
    );
    return response;
  },

  getNotConsultedCount: async (companyId: string): Promise<NotConsultedCountDto> => {
    const { data: response } = await apiClient.get<NotConsultedCountDto>(
      `/companies/${companyId}/dashboard/not-consulted-count`,
    );
    return response;
  },

  getRecentDocuments: async (companyId: string): Promise<DashboardRecentDocumentsDto> => {
    const { data: response } = await apiClient.get<DashboardRecentDocumentsDto>(
      `/companies/${companyId}/dashboard/recent-documents`,
    );
    return response;
  },

  getUserDocuments: async (companyId: string): Promise<UserDocumentsDto> => {
    const { data: response } = await apiClient.get<UserDocumentsDto>(
      `/companies/${companyId}/dashboard/user-documents`,
    );
    return response;
  },

  getDiffusedDocuments: async (companyId: string): Promise<FilteredDocumentsDto> => {
    const { data: response } = await apiClient.get<FilteredDocumentsDto>(
      `/companies/${companyId}/dashboard/documents/diffused`,
    );
    return response;
  },

  getValidatedDocuments: async (companyId: string): Promise<FilteredDocumentsDto> => {
    const { data: response } = await apiClient.get<FilteredDocumentsDto>(
      `/companies/${companyId}/dashboard/documents/validated`,
    );
    return response;
  },

  getPendingDocuments: async (companyId: string): Promise<FilteredDocumentsDto> => {
    const { data: response } = await apiClient.get<FilteredDocumentsDto>(
      `/companies/${companyId}/dashboard/documents/pending`,
    );
    return response;
  },

  getNotConsultedDocuments: async (companyId: string): Promise<FilteredDocumentsDto> => {
    const { data: response } = await apiClient.get<FilteredDocumentsDto>(
      `/companies/${companyId}/dashboard/documents/not-consulted`,
    );
    return response;
  },
};

export const useGetDiffusedCount = (companyId: string) => {
  return useQuery({
    queryKey: ['dashboard-diffused-count', companyId],
    queryFn: () => dashboardApi.getDiffusedCount(companyId),
    enabled: !!companyId,
  });
};

export const useGetValidatedCount = (companyId: string) => {
  return useQuery({
    queryKey: ['dashboard-validated-count', companyId],
    queryFn: () => dashboardApi.getValidatedCount(companyId),
    enabled: !!companyId,
  });
};

export const useGetOpenedRatio = (companyId: string) => {
  return useQuery({
    queryKey: ['dashboard-opened-ratio', companyId],
    queryFn: () => dashboardApi.getOpenedRatio(companyId),
    enabled: !!companyId,
  });
};

export const useGetNotConsultedCount = (companyId: string) => {
  return useQuery({
    queryKey: ['dashboard-not-consulted-count', companyId],
    queryFn: () => dashboardApi.getNotConsultedCount(companyId),
    enabled: !!companyId,
  });
};

export const useGetRecentDocuments = (companyId: string) => {
  return useQuery({
    queryKey: ['dashboard-recent-documents', companyId],
    queryFn: () => dashboardApi.getRecentDocuments(companyId),
    enabled: !!companyId,
  });
};

export const useGetUserDocuments = (companyId: string) => {
  return useQuery({
    queryKey: ['dashboard-user-documents', companyId],
    queryFn: () => dashboardApi.getUserDocuments(companyId),
    enabled: !!companyId,
  });
};

export const useGetDiffusedDocuments = (companyId: string) => {
  return useQuery({
    queryKey: ['dashboard-diffused-documents', companyId],
    queryFn: () => dashboardApi.getDiffusedDocuments(companyId),
    enabled: !!companyId,
  });
};

export const useGetValidatedDocuments = (companyId: string) => {
  return useQuery({
    queryKey: ['dashboard-validated-documents', companyId],
    queryFn: () => dashboardApi.getValidatedDocuments(companyId),
    enabled: !!companyId,
  });
};

export const useGetPendingDocuments = (companyId: string) => {
  return useQuery({
    queryKey: ['dashboard-pending-documents', companyId],
    queryFn: () => dashboardApi.getPendingDocuments(companyId),
    enabled: !!companyId,
  });
};

export const useGetNotConsultedDocuments = (companyId: string) => {
  return useQuery({
    queryKey: ['dashboard-not-consulted-documents', companyId],
    queryFn: () => dashboardApi.getNotConsultedDocuments(companyId),
    enabled: !!companyId,
  });
};
